import { Component, OnInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { ChartOptions, ChartDataset } from 'chart.js';
import moment from 'moment-timezone';

import { ActionDropdownComponent } from 'src/app/components/action-dropdown/action-dropdown.component';
import { downloadCSV } from 'src/app/utils/utils';
import { Customer } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

const MAX_BUBBLE_SIZE = 22;
const DAY_OF_WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatHours(hour: number) {
  const suffix = hour < 12 ? 'AM' : 'PM';

  return ((hour + 11) % 12) + 1 + suffix;
}

function getLabel(y: number, x: number) {
  const hours = formatHours(x);

  return `${DAY_OF_WEEK_LABELS[y]}, ${hours}`;
}

@Component({
  selector: 'app-admin-analytics',
  templateUrl: './admin-analytics.component.html',
  styleUrls: ['./admin-analytics.component.css'],
})
export class AdminAnalyticsComponent implements OnInit {
  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private popoverCtrl: PopoverController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  public isLoading: boolean;
  public timezone: string;
  public dayOfWeekLabels = DAY_OF_WEEK_LABELS;
  public dateFilterLabel = 'Last 4 weeks';
  public dateFilterWeeks = 4;
  public userIdFilter: string;
  public userLabel: string = 'Sent by';
  public isExternal = true;

  public customers: Partial<Customer>[] = [];
  private customerNoiseGraphRawData = new Map<
    string,
    { i: number; customer: Partial<Customer>; data: Record<string, number> }
  >();
  public customerNoiseGraphDatasets: ChartDataset[] = [];
  public customerNoiseGraphOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => {
            const customer = this.customers[tooltipItem.parsed.y];
            const totalCount = this.customerNoiseGraphRawData.get(customer.id).data[tooltipItem.parsed.x];

            return `${customer?.name}, ${moment(tooltipItem.parsed.x).format('MMM D')} - ${totalCount ?? 0} messages`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1,
        },
        time: {
          unit: 'day',
          displayFormats: {
            hour: 'DD',
          },
        },
      },
      y: {
        min: -1,
        max: 0,
        reverse: true,
        ticks: {
          maxTicksLimit: 2,
          stepSize: 1,
          callback: (i) => this.customers[i]?.name,
        },
      },
    },
  };

  private messagesByPeriodResponse: any;
  private messagesByPeriodRawData = [[], [], [], [], [], [], []];
  public messagesByPeriodDatasets: ChartDataset[] = [];
  public messagesByPeriodOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => {
            console.log(tooltipItem);

            const y = Number(tooltipItem.parsed.y);
            const x = Number(tooltipItem.parsed.x);
            const averageCount = this.messagesByPeriodRawData[y][x] ?? 0;

            return `${getLabel(y, x)}: ${averageCount} message${averageCount === 1 ? '' : 's'} on average`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1,
          callback: formatHours,
        },
        time: {
          parser: 'HH',
        },
      },
      y: {
        min: -1,
        max: 7,
        reverse: true,
        ticks: {
          maxTicksLimit: 9,
          stepSize: 1,
          callback: (value) => DAY_OF_WEEK_LABELS[value] ?? '',
        },
      },
    },
  };

  private messagesByDowResponse: any;
  public messagesByDowDatasets: ChartDataset[] = [];
  public messagesByDowOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.formattedValue} new message${
              tooltipItem.formattedValue === '1' ? '' : 's'
            } per day on average`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1,
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  public messagesByUserDatasets: ChartDataset[] = [];
  public messagesByUserOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.formattedValue} new message${
              tooltipItem.formattedValue === '1' ? '' : 's'
            } per day on average`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          stepSize: 1,
        },
      },
      y: {
        display: false,
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  public totalCount = 0;

  async ngOnInit() {
    this.timezone = moment().tz(moment.tz.guess()).format('z');

    this.getReports();
  }

  async getReports() {
    this.isLoading = true;

    // Clear out previous data
    this.messagesByPeriodRawData = [[], [], [], [], [], [], []];
    this.messagesByPeriodDatasets = [];

    await Promise.all([this.getMessageCountByPeriod(), this.getMessageCountByDow()]);
    this.isLoading = false;
  }

  getQueryParams() {
    const userQuery = this.userIdFilter ? `&userId=${this.userIdFilter}` : '';
    return `timezone=${this.timezone}&numberOfWeeks=${this.dateFilterWeeks}${userQuery}&isExternal=${this.isExternal}`;
  }

  async getMessageCountByPeriod() {
    this.messagesByPeriodResponse = await this.apiService.getPromise(
      `/reporting/message-count-by-period?${this.getQueryParams()}`,
    );
    let maxAverageCount = 0;

    for (const point of this.messagesByPeriodResponse) {
      if (point.averageCount) {
        this.messagesByPeriodRawData[point.dow][point.hour] = point.averageCount;

        if (point.averageCount > maxAverageCount) {
          maxAverageCount = point.averageCount;
        }
      }
    }

    for (let y = 0; y < 7; y++) {
      for (let x = 0; x < 24; x++) {
        const averageCount = this.messagesByPeriodRawData[y][x];

        this.messagesByPeriodDatasets.push({
          label: getLabel(y, x),
          backgroundColor: 'rgb(207, 2, 23)',
          data: [
            {
              x: Number(String(x).padStart(2, '0')),
              y: y,
              r: maxAverageCount > 0 ? (averageCount / maxAverageCount) * MAX_BUBBLE_SIZE : 0,
            },
          ],
        });
      }
    }
  }

  async getMessageCountByDow() {
    this.messagesByDowResponse = await this.apiService.getPromise(
      `/reporting/message-count-by-period?groupBy=dow&${this.getQueryParams()}`,
    );
    const messagesByDowRawData = {};
    let total = 0;

    for (const point of this.messagesByDowResponse) {
      if (point.averageCount) {
        messagesByDowRawData[point.dow] = point.averageCount;
      }
      if (point.totalCount) {
        total += point.totalCount;
      }
    }

    const messagesByDowData = [];

    for (let x = 0; x < 7; x++) {
      const averageCount = messagesByDowRawData[x];

      messagesByDowData.push(averageCount);
    }

    this.totalCount = total;
    this.messagesByDowDatasets = [{ data: messagesByDowData, backgroundColor: 'rgba(34, 57, 237, 0.8)' }];
  }

  showUserOptions() {
    this.searchablePopoverService.users({
      event,
      selectedUserId: this.userIdFilter,
      callback: (id: string, name: string) => {
        if (id) {
          this.userIdFilter = id;
          this.userLabel = `Sent by: ${name}`;
        } else {
          this.userIdFilter = undefined;
          this.userLabel = `Sent by`;
        }

        this.getReports();
      },
    });
  }

  async showDateOptions() {
    const options = [
      {
        label: 'Last 4 weeks',
        metadata: {
          weeks: 4,
        },
      },
      {
        label: 'Last 12 weeks',
        metadata: {
          weeks: 12,
        },
      },
      {
        label: 'Last 6 months',
        metadata: {
          weeks: 26,
        },
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          this.dateFilterLabel = options[idx].label;
          this.dateFilterWeeks = options[idx].metadata.weeks;

          this.getReports();
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  downloadDowData() {
    downloadCSV(
      this.messagesByDowResponse,
      ['dow', 'averageCount', 'totalCount'],
      `message-volume-period-${this.dateFilterLabel.toLowerCase().replaceAll(' ', '-')}`,
    );
  }

  downloadPeriodData() {
    downloadCSV(
      this.messagesByPeriodResponse,
      ['hour', 'dow', 'averageCount', 'totalCount'],
      `message-volume-period-${this.dateFilterLabel.toLowerCase().replaceAll(' ', '-')}`,
    );
  }

  /*
   * Taken out because we couldn't handle long customer lists
   *
  async getCustomerNoiseGraph() {
    const yAxisTicks = this.customerNoiseGraphOptions.scales.yAxes[0].ticks;

    yAxisTicks.max = this.customers.length;
    yAxisTicks.maxTicksLimit = this.customers.length + 2;

    const data: any[] = await this.apiService.getPromise(`/reporting/customer-noise-graph?timezone=${this.timezone}`);
    let maxTotalCount = 0;

    for (const point of data) {
      if (point.totalCount) {
        const customerRawData = this.customerNoiseGraphRawData.get(point.customerId);

        if (customerRawData) {
          customerRawData.data[point.day.substring(0, 10)] = point.totalCount;
        }

        if (point.totalCount > maxTotalCount) {
          maxTotalCount = point.totalCount;
        }
      }
    }

    for (let m = moment().startOf('day').subtract(30, 'days'); m.isBefore(moment()); m.add(1, 'day')) {
      const day = m.format('YYYY-MM-DD');

      for (const customerRawData of this.customerNoiseGraphRawData.values()) {
        if (customerRawData) {
          const totalCount = customerRawData?.data[day] ?? 0;

          this.customerNoiseGraphDatasets.push({
            backgroundColor: 'rgb(143, 0, 209)',
            data: [
              {
                x: day,
                y: customerRawData.i,
                r: maxTotalCount > 0 ? (totalCount / maxTotalCount) * MAX_BUBBLE_SIZE : 0,
              },
            ],
          });
        }
      }
    }
  }
  */
}
